/*
 * Adapted from fdlibm via Node v22.16.0's V8 src/base/ieee754.cc:
 * https://github.com/nodejs/node/blob/v22.16.0/deps/v8/src/base/ieee754.cc
 *
 * Copyright (C) 1993 by Sun Microsystems, Inc. All rights reserved.
 * Developed at SunSoft, a Sun Microsystems, Inc. business.
 * Permission to use, copy, modify, and distribute this
 * software is freely granted, provided that this notice
 * is preserved.
 *
 * The original source code covered by the above license has been
 * modified significantly by Google Inc.
 * Copyright 2016 the V8 project authors. All rights reserved.
 */
/**
 * deterministicSin — sin(x) that returns the same bits on every CPU and
 * JavaScript engine, equal to x86_64 V8's Math.sin.
 *
 * WHY THIS EXISTS. Boundary areas are hash material for the study-area pins,
 * the study-area selection hash and the Generation & Impact frozen boundary,
 * and a hash made in the browser is checked again on Cloud Run. Math.sin is not
 * the same function everywhere:
 *   - V8 (Node, Cloud Run) computes it with fdlibm compiled from C++
 *     (src/base/ieee754.cc). The arm64 build fuses multiply-adds; the x86_64
 *     build does not. About 1% of inputs differ in the last bit.
 *   - Chromium builds V8 with a different (glibc-derived) sin, and Safari and
 *     Firefox use their own libraries, so the result depends on the engine as
 *     well as the CPU.
 * One such bit made Pineywoods p2 hash to cc418b15 in a Mac browser and to
 * 6a4f3139 on Cloud Run (2026-10-13), and the SED rebuild refused the pin.
 *
 * WHAT IT IS. A line-for-line port of fdlibm's sin, __kernel_sin, __kernel_cos
 * and the |x| < 3pi/4 branch of __ieee754_rem_pio2, as in V8. ECMAScript rounds
 * every + - * / separately and never fuses them, so this returns exactly what
 * x86_64 V8 returns: the values Cloud Run has always computed. No hash Cloud
 * Run has made changes.
 *
 * RESTRICTED ARITHMETIC (same rule as generationImpact/gasInterconnectDerate.ts
 * and generationImpact/maxPowerCeilings.ts). Only + - * /, comparisons and the
 * DataView word reads below. Never reorder, merge or simplify an expression
 * (for example x*x*x for v, or folding constants), never replace the
 * polynomial with Math.sin or Math.cos, and keep the output bit-identical to
 * the golden vectors and digest in
 * src/lib/__tests__/siteGeometryPlatformIndependence.test.ts. A change that
 * moves one output bit changes every hash built from it.
 *
 * DOMAIN. |x| < 3pi/4, which covers every latitude in radians. Outside it
 * (and for NaN and Infinity) this defers to Math.sin. Hashed boundaries never
 * get there: the boundary authority rejects |lat| > 85 (cleanRing in
 * generationImpact/boundaryAuthority.ts).
 */

// Word access is big-endian through a DataView, so it does not depend on the
// host's byte order.
const view = new DataView(new ArrayBuffer(8));

/** fdlibm GET_HIGH_WORD: the signed upper 32 bits of x. */
function highWord(x: number): number {
  view.setFloat64(0, x);
  return view.getInt32(0);
}

/** fdlibm INSERT_WORDS. */
function wordsToDouble(high: number, low: number): number {
  view.setInt32(0, high);
  view.setInt32(4, low);
  return view.getFloat64(0);
}

const S1 = -1.66666666666666324348e-01; // 0xBFC55555 0x55555549
const S2 = 8.33333333332248946124e-03; // 0x3F811111 0x1110F8A6
const S3 = -1.98412698298579493134e-04; // 0xBF2A01A0 0x19C161D5
const S4 = 2.75573137070700676789e-06; // 0x3EC71DE3 0x57B1FE7D
const S5 = -2.50507602534068634195e-08; // 0xBE5AE5E6 0x8A2B9CEB
const S6 = 1.58969099521155010221e-10; // 0x3DE5D93A 0x5ACFD57C

/** fdlibm __kernel_sin on [-pi/4, pi/4]; y is the tail of x, iy says whether y is 0. */
function kernelSin(x: number, y: number, iy: 0 | 1): number {
  const ix = highWord(x) & 0x7fffffff;
  if (ix < 0x3e400000 && (x | 0) === 0) return x; // |x| < 2^-27
  const z = x * x;
  const v = z * x;
  const r = S2 + z * (S3 + z * (S4 + z * (S5 + z * S6)));
  if (iy === 0) return x + v * (S1 + z * r);
  return x - ((z * (0.5 * y - v * r) - y) - v * S1);
}

const C1 = 4.16666666666666019037e-02; // 0x3FA55555 0x5555554C
const C2 = -1.38888888888741095749e-03; // 0xBF56C16C 0x16C15177
const C3 = 2.48015872894767294178e-05; // 0x3EFA01A0 0x19CB1590
const C4 = -2.75573143513906633035e-07; // 0xBE927E4F 0x809C52AD
const C5 = 2.08757232129817482790e-09; // 0x3E21EE9E 0xBDB4B1C4
const C6 = -1.13596475577881948265e-11; // 0xBDA8FAE9 0xBE8838D4

/** fdlibm __kernel_cos on [-pi/4, pi/4]; y is the tail of x. */
function kernelCos(x: number, y: number): number {
  const ix = highWord(x) & 0x7fffffff;
  if (ix < 0x3e400000 && (x | 0) === 0) return 1; // |x| < 2^-27
  const z = x * x;
  const r = z * (C1 + z * (C2 + z * (C3 + z * (C4 + z * (C5 + z * C6)))));
  if (ix < 0x3fd33333) return 1 - (0.5 * z - (z * r - x * y)); // |x| < 0.3
  const qx = ix > 0x3fe90000 ? 0.28125 : wordsToDouble(ix - 0x00200000, 0); // x > 0.78125 ? 0.28125 : x/4
  const iz = 0.5 * z - qx;
  const a = 1 - qx;
  return a - (iz - (z * r - x * y));
}

const PIO2_1 = 1.57079632673412561417e+00; // 0x3FF921FB 0x54400000, first 33 bits of pi/2
const PIO2_1T = 6.07710050650619224932e-11; // 0x3DD0B461 0x1A626331, pi/2 - PIO2_1
const PIO2_2 = 6.07710050630396597660e-11; // 0x3DD0B461 0x1A600000, second 33 bits of pi/2
const PIO2_2T = 2.02226624879595063154e-21; // 0x3BA3198A 0x2E037073, pi/2 - (PIO2_1 + PIO2_2)

/** sin(x), bit-identical to x86_64 V8 Math.sin for |x| < 3pi/4 on every host. */
export function deterministicSin(x: number): number {
  const hx = highWord(x);
  const ix = hx & 0x7fffffff;
  if (ix <= 0x3fe921fb) return kernelSin(x, 0, 0); // |x| ~<= pi/4
  if (ix >= 0x4002d97c) return Math.sin(x); // |x| >= 3pi/4, NaN, Infinity: outside every latitude
  // __ieee754_rem_pio2 for pi/4 < |x| < 3pi/4, where n = +1 or -1.
  let y0: number;
  let y1: number;
  if (hx > 0) {
    let z = x - PIO2_1;
    if (ix !== 0x3ff921fb) { // 33+53-bit pi/2 is good enough
      y0 = z - PIO2_1T;
      y1 = (z - y0) - PIO2_1T;
    } else { // near pi/2: use 33+33+53-bit pi/2
      z -= PIO2_2;
      y0 = z - PIO2_2T;
      y1 = (z - y0) - PIO2_2T;
    }
    return kernelCos(y0, y1); // n = 1
  }
  let z = x + PIO2_1;
  if (ix !== 0x3ff921fb) {
    y0 = z + PIO2_1T;
    y1 = (z - y0) + PIO2_1T;
  } else {
    z += PIO2_2;
    y0 = z + PIO2_2T;
    y1 = (z - y0) + PIO2_2T;
  }
  return -kernelCos(y0, y1); // n = -1
}
